import axios from 'axios'
import useLocalStorage from './useLocalStorage'

import useRegex from './regex'

import type { LoyaltyMerchantFormMedia } from '@/types/global'
import type { MerchantMedias } from '@/types/types'

const merchantMedias = ref(<MerchantMedias>{})

export default function useMerchantMedias() {
  const { user } = useLocalStorage()
  const notification = useNotification()
  const { t } = useI18n()
  const { isUrlValid } = useRegex()

  const getMerchantMedias = async (): Promise<MerchantMedias> => {
    try {
      const { data } = await axios.get(`/merchants/${user.value.merchant.id}/media`)
      merchantMedias.value = data.data
      return data.data
    } catch {
      notification.create('error', {
        text: t('pages.settings.merchantForm.notifications.get.merchantMediaError'),
        timeout: 3000
      })
    }
  }

  const tabletImageNames = computed(() => ['coverA', 'coverB', 'coverC', 'coverD'])

  const merchantMediaForm = ref<LoyaltyMerchantFormMedia>({
    logo: {
      type: 'image',
      isCroppable: false,
      name: 'logo',
      label: 'Logo',
      pictureUrl: null,
      allowCrop: false,
      rules: 'image'
    },
    logoMail: {
      type: 'image',
      isCroppable: false,
      name: 'logoMail',
      label: 'Logo mail',
      pictureUrl: null,
      allowCrop: false,
      rules: 'image'
    },
    coverA: {
      type: 'image',
      isCroppable: false,
      name: 'coverA',
      label: 'Cover A',
      pictureUrl: null,
      allowCrop: true,
      rules: 'image'
    },
    coverB: {
      type: 'image',
      isCroppable: false,
      name: 'coverB',
      label: 'Cover B',
      pictureUrl: null,
      allowCrop: true,
      rules: 'image'
    },
    coverC: {
      type: 'image',
      isCroppable: false,
      name: 'coverC',
      label: 'Cover C',
      pictureUrl: null,
      allowCrop: true,
      rules: 'image'
    },
    coverD: {
      type: 'image',
      isCroppable: false,
      name: 'coverD',
      label: 'Cover D',
      pictureUrl: null,
      allowCrop: true,
      rules: 'image'
    }
  })

  const updateMerchantFormMedias = async () => {
    const params = Object.keys(merchantMediaForm.value).reduce((acc, key) => {
      const media = merchantMediaForm.value[key]
      acc[media.name] = isUrlValid(media.pictureUrl) ? null : media.pictureUrl
      return acc
    }, {})

    try {
      await axios.put(`merchants/${user.value.merchant.id}/media`, {
        ...params
      })
    } catch {
      notification.create('error', {
        text: t('pages.settings.merchantForm.notifications.updateMerchantMedia.error'),
        timeout: 3000
      })
    }
  }

  return {
    getMerchantMedias,
    merchantMedias,
    tabletImageNames,
    merchantMediaForm,
    updateMerchantFormMedias
  }
}
